import { Injectable } from '@angular/core';

import API, { graphqlOperation } from '@aws-amplify/api';
import { openAuctions,
         auctions,
         auctionLot,
         auctionsBySeller,
         auctionReport,
         bidderReport } from '../../graphql/queries';

import { submitBid,
         createAuction,
         updateAuction,
         deleteAuction,
         addAuctionBusiness,
         deleteAuctionBusiness,
         manageFile,
         deleteAuctionAndAssets,
         cloneAuctionAndAssets,
         cloneAuctionLotAndAssets,
         updateAuctionLot,
         createAuctionLot,
         deleteAuctionLot,
         updateAuctionReportLineItem,
         splitAuctionReportLineItem,
         bulkUpdateAuctionReportLineItems,
         sendInvoices,
         sendInvoice,
         updateLotOrder,
         initiateTask,
         updateAuctionReport,
         deleteBid
       } from '../../graphql/mutations';
import { addAuctionAuctioneer, deleteAuctionAuctioneer } from '../../graphql/auctioneer';

import { onAuctionChanged,
         onTaskProgressUpdated  } from '../../graphql/subscriptions';

import { auctionCustom,
         openAuctionsCustom,
         auctionLotCustom,
         auctionLotBidsCustom,
         updateAuctionLotPriceParmsCustom,
         auctionBiddersCustom,
         auctionLotsBidPriceCustom,
         auctionLotsBidTimeCustom,
         auctionLotVersionCustom,
         auctionLotCountsCustom,
         updateLotNumberCustom,
         auctionReportCustom,
         allAuctionsCustom,
         auctionAllLotBidsCustom } from '../../graphql/custom';

import { OpenAuctionsQuery,
         AuctionQuery,
         SubmitBidMutation,
         BidResultStatus,
         BidResultCode,
         AuctionLocationType,
         AuctionLotQuery,
         OpenAuctionsCustomQuery,
         AuctionLotCustomQuery,
         DeleteAuctionMutation,
         UpdateAuctionLotMutation,
         CreateAuctionMutation,
         ClosingExtensionStyle,
         AuctionType,
         CommissionType,
         BankAccountType,
         AuctionStatus,
         AuctionLotCategory,
         SexedCategory,
         EPDCategory,
         PhoneNumberDetailInput,
         UpdateAuctionMutation,
         AddAuctionBusinessMutation,
         DeleteAuctionBusinessMutation,
         ManageFileMutation,
         AuctionsBySellerQuery,
         DeleteAuctionAndAssetsMutation,
         CloneAuctionAndAssetsMutation,
         CloneAuctionLotAndAssetsMutation,
         BidType,
         AuctionLotBidsCustomQuery,
         CreateAuctionLotMutation,
         DeleteAuctionLotMutation,
         UpdateAuctionLotPriceParmsCustomMutation,
         AuctionBiddersCustomQuery,
         AuctionLotsBidPriceCustomQuery,
         AuctionLotsBidTimeCustomQuery,
         OnAuctionChangedSubscriptionVariables,
         AuctionLotVersionCustomQuery,
         AuctionLotCountsCustomQuery,
         AuctionReportCustomQuery,
         AuctionReportSortField,
         AuctionReportCustomQueryVariables,
         SortDirection,
         UpdateAuctionReportLineItemMutationVariables,
         UpdateAuctionReportLineItemMutation,
         SendInvoicesMutationVariables,
         SendInvoicesMutation,
         SendInvoiceMutationVariables,
         SendInvoiceMutation,
         InterestType,
         ExportableType,
         UpdateLotOrderMutation,
         UpdateAuctionLotNumCustomMutation,
         PaymentStatus,
         SaleStatus,
         InitiateTaskMutation,
         OnTaskProgressUpdatedSubscriptionVariables,
         AllAuctionsCustomQuery,
         AuctionReportPaymentInput,
         UpdateAuctionReportMutationVariables,
         UpdateAuctionReportMutation,
         SplitAuctionReportLineItemMutationVariables,
         SplitAuctionReportLineItemMutation,
         SplitAuctionReportLineItemInput,
         BulkUpdateAuctionReportLineItemsMutationVariables,
         BulkUpdateAuctionReportLineItemsMutation,
         AuctionAllLotBidsCustomQuery,
         DeleteBidMutation,
         BidderReportQuery
        } from '../API.service';

import { environment } from '../../environments/environment'
import { PortalUser } from './portaluser.model';
import { AuctionTimeStatistics } from '../services/auction-time-statistics';
import { SplitUser } from '../auctions/auction-report-split-dialog/auction-report-split-dialog.component';

export interface LineageHierarchy {
  sire:string;
  sireRegNum:string;
  sireUrl:string;
  dam:string;
  damRegNum:string;
  damUrl:string;
  sireParents:LineageHierarchy;
  damParents:LineageHierarchy;
}

export interface EPDMeasures {
  category:EPDCategory;
  measure:string;
  value:string;
  accuracy:string;
  progeny:string;
  percentage:string;
  order:string;
}

export interface CustomLotField {
  customLabel:string;
  customValue:string;
}

export interface LotImage {
  storageName: string;
  fileName: string;
  caption: string;
  cover: boolean;
}

@Injectable()
export class AuctionModelService {

  openAuctionsList:AuctionModel[] = [];
  auctionsBySellerList:AuctionModel[] = [];
  activeAuction:AuctionModel;
  activeLot:LotModel;
  subscriptions: any[];
  taskSubscriptions: any[];

  // lotSummaryList:LotModel[] = [];
  activeAuctionReport: AuctionReportModel;

  constructor() {
    this.subscriptions = [];
    this.taskSubscriptions = [];
  }

  async loadOpenAuctions(forcereload=false,sortDirection="ASC") {
    let returnValue = "";
    if(this.openAuctionsList.length == 0 || forcereload == true) {
      this.openAuctionsList = [];
      let queryVars = {};
      queryVars['limit'] = 50;
      queryVars['sortDirection'] = sortDirection;
      let continueLoop = true;

      while(continueLoop){
        try{
          const response = (await API.graphql(graphqlOperation(allAuctionsCustom,queryVars))) as {
            data: AllAuctionsCustomQuery;
          };
          // console.log('openAuctionsCustom',response);
          for(let item of response.data.auctions.items){
            let auction = new AuctionModel();
            auction.load(item);
            this.openAuctionsList.push(auction);
          }
          continueLoop = response.data.auctions.nextToken != undefined;
          queryVars['nextToken'] = response.data.auctions.nextToken;
        }
        catch(err){
          console.log('error loading open auctions list',err);
          throw(err)
        }
      }
    }
    return this.openAuctionsList;
  }

  async loadAuctionsBySeller(sellerId:string,forcereload=false,sortDirection="ASC") {
    let returnValue = "";
    if(this.auctionsBySellerList.length == 0 || forcereload == true) {
      this.auctionsBySellerList = [];
      let queryVars = {};
      queryVars['sellerId'] = sellerId;
      queryVars['limit'] = 20;
      queryVars['sortDirection'] = sortDirection;
      let continueLoop = true;

      while(continueLoop){
        try{
          const response = (await API.graphql(graphqlOperation(auctionsBySeller,queryVars))) as {
            data: AuctionsBySellerQuery;
          };
          // console.log('response',response);
          for(let item of response.data.auctionsBySeller.items){
            let auction = new AuctionModel();
            auction.load(item);
            this.auctionsBySellerList.push(auction);
          }
          continueLoop = response.data.auctionsBySeller.nextToken != undefined;
          queryVars['nextToken'] = response.data.auctionsBySeller.nextToken;
        }
        catch(err){
          console.log('error loading auctions by seller',err);
          throw(err)
        }
      }
    }
    return this.auctionsBySellerList;
  }

  async loadAuctionsLotsByRecentBidActivity(auctionId:string,limit:number=5,sortDirection="DESC") {
    // this.lotSummaryList.splice(0,this.lotSummaryList.length);
    let returnValue = new Array();
    let queryVars = {};
    queryVars['auctionId'] = auctionId;
    queryVars['limit'] = limit;
    queryVars['sortDirection'] = sortDirection;

    try{
      const response = (await API.graphql(graphqlOperation(auctionLotsBidTimeCustom,queryVars))) as {
        data: AuctionLotsBidTimeCustomQuery;
      };
      // console.log('auctionLotsBidTimeCustom',response);
      for(let item of response.data.auctionLotsBidTime.items){
        let lot = new LotModel();
        lot.load(item);
        returnValue.push(lot);
      }
    }
    catch(err){
      console.log('error loading lots by recent bid activity',err);
      throw(err)
    }
    return returnValue;
  }

  async loadAuctionsLotsByRecentBidPrice(auctionId:string,limit:number=5,sortDirection="ASC") {
    // this.lotSummaryList.splice(0,this.lotSummaryList.length);
    let returnValue = new Array();

    let queryVars = {};
    queryVars['auctionId'] = auctionId;
    queryVars['limit'] = 5;
    queryVars['sortDirection'] = sortDirection;

    try{
      const response = (await API.graphql(graphqlOperation(auctionLotsBidPriceCustom,queryVars))) as {
        data: AuctionLotsBidPriceCustomQuery;
      };
      // console.log('auctionLotsBidPriceCustom',response);
      for(let item of response.data.auctionLotsBidPrice.items){
        let lot = new LotModel();
        lot.load(item);
        returnValue.push(lot);
      }
    }
    catch(err){
      console.log('error loading lots by recent bid price',err);
      throw(err)
    }
    return returnValue;
  }

  async getAuctionAllLotBids(auctionId:string){
    let returnValue = new Array();
    try{
      var myAuction = new AuctionModel();
      await myAuction.getAllLotsForBidsActivity(auctionId);
      returnValue = myAuction.lots;
    }
    catch(err){
      console.log('error loading auction lots',err);
      throw(err)
    }
    return returnValue;
  }

  async loadAuction(auctionId:string) {
    let queryVars = {};
    queryVars['auctionId'] = auctionId;

    try{
      const response = (await API.graphql(graphqlOperation(auctionCustom,queryVars))) as {
        data: AuctionQuery;
      };
      // console.log('AuctionQuery',response.data);
      this.activeAuction = new AuctionModel();
      this.activeAuction.load(response.data.auction);
    }
    catch(err){
      console.log('error loading auction',err);
      throw(err)
    }
    return this.activeAuction;
  }

  async loadLot(auctionId:string, lotId:string) {
    let queryVars = {};
    queryVars['auctionId'] = auctionId;
    queryVars['lotId'] = lotId;

    try{
      const response = (await API.graphql(graphqlOperation(auctionLotCustom,queryVars))) as {
        data: AuctionLotCustomQuery;
      };
      // console.log('AuctionLotCustomQuery',response.data);
      this.activeLot = new LotModel();
      this.activeLot.load(response.data.auctionLot);
    }
    catch(err){
      console.log('error retrieving lot',err);
      throw(err)
    }
    return this.activeLot;
  }

  async subscribeToAuctionUpdates(auctionId: string, localThis, onUpdate: (bidData,localThis) => void, onError?: (message: string) => void) {
    try {
      const variables: OnAuctionChangedSubscriptionVariables = { auctionId: auctionId }
      const operation = await API.graphql(graphqlOperation(onAuctionChanged, variables))
      // @ts-ignore https://github.com/aws-amplify/amplify-js/issues/5741
      const subscription = operation.subscribe({
        next: (bidData) => {
          onUpdate(bidData.value.data.onAuctionChanged,localThis);
        },
        error: (error: any) => {
          if (onError) onError(error)
          console.error(error)
        },
      })
      let key = Date.now();
      let subscriptionParms = {
        "key":key,
        "subscription":subscription
      }
      this.subscriptions.push(subscriptionParms)
      return subscriptionParms
    }
    catch (e) {
      throw(e)
    }
  }
​
  unsubscribeToAuctionUpdates() {
    try {
      this.subscriptions.forEach((el) => {
        el.subscription.unsubscribe()
      })
      this.subscriptions = []
    }
    catch (e) {
      console.error(e)
    }
  }

  unsubscribeToSingleBidUpdate(key) {
    let index = this.subscriptions.findIndex((el)=>{return el.key==key});
    if(index >= 0){
      // console.log('calling unsubscribe',key);
      try {
        this.subscriptions[index]['subscription'].unsubscribe();
        this.subscriptions.splice(index,1);
      }
      catch (e) {
        console.error(e)
      }

    }
  }

  public async loadAuctionReport(
    auctionId: string,
    sortField: string,
    sortDirection: string,
    lotLimit: number,
    lastItemId: string
  ) {
    let model = new AuctionReportModel();
    const auctionReport = await model.get(
      auctionId,
      sortField,
      sortDirection,
      lotLimit,
      lastItemId,
    );
    this.activeAuctionReport = auctionReport;

    return auctionReport;
  }

  initNewLot(auctionId:string){
    let returnValue = new LotModel();
    returnValue.auctionId = auctionId;
    returnValue.biddingDisabled = false;
    returnValue.title = "";
    returnValue.footnote = [];
    returnValue.footnote.push("");
    if(this.activeAuction.lots == undefined || this.activeAuction.lots.length==0){
      returnValue.lotOrder = 1;
    }
    else {
      let maxLotOrder = this.activeAuction.lots.slice(-1)[0].lotOrder;
      returnValue.lotOrder = maxLotOrder + 1;
    }
    returnValue.lotNum = "" + returnValue.lotOrder;
    returnValue.startBid = this.activeAuction.startBid != undefined ? this.activeAuction.startBid : undefined;
    returnValue.floorPrice = this.activeAuction.floorPrice != undefined ? this.activeAuction.floorPrice : undefined;
    returnValue.bidIncrement = this.activeAuction.bidIncrement != undefined ? this.activeAuction.bidIncrement : undefined;
    returnValue.openBiddingPeriodMinutes = this.activeAuction.defaultLotOpenBiddingPeriodMinutes;
    returnValue.photos = [];
    returnValue.videos = [];
    returnValue.interest = InterestType.WHOLE;
    returnValue.lineageHierarchy= returnValue.getInitializedLineage();
    returnValue.epdMeasures = returnValue.getInitializedEPDs();
    returnValue.actualEPD = true;
    return returnValue;
  }

  async subscribeToTask(taskId: string, localThis, onUpdate: (taskData,localThis) => void, onError?: (message: string) => void) {
    try {
      const variables: OnTaskProgressUpdatedSubscriptionVariables = { id: taskId }
      const operation = await API.graphql(graphqlOperation(onTaskProgressUpdated, variables))
      // @ts-ignore https://github.com/aws-amplify/amplify-js/issues/5741
      const subscription = operation.subscribe({
        next: (taskData) => {
          onUpdate(taskData.value.data.onTaskProgressUpdated,localThis);
        },
        error: (error: any) => {
          if (onError) onError(error)
          console.error(error)
        },
      })
// ​     console.log('subscription',subscription);
      let subscriptionParms = {
        "key":taskId,
        "subscription":subscription
      }
      this.taskSubscriptions.push(subscriptionParms)
      return subscriptionParms
    }
    catch (e) {
      throw(e)
    }
  }
​
  unsubscribeToTaskUpdate(taskId:string) {
    try {
      let taskIndex = this.taskSubscriptions.findIndex((el)=>{return el.taskId == taskId});
      if(taskIndex != -1){
        this.taskSubscriptions[taskIndex].subscription.unscribe();
        this.taskSubscriptions.splice(taskIndex,1);
      }
    }
    catch (e) {
      console.error(e)
    }
  }

}

export class AuctionModel {

  static AUCTION_TYPES = [
    {"key":"HORSE_STYLE","value":"Horse Style"},
    {"key":"SHOT_CLOCK","value":"Shot Clock"}
    // {"key":"LIVE_CLOSEOUT","value":"Live Closeout"},
    // {"key":"STAGGERED_CLOSE","value":"Staggered Close"},
    // {"key":"MARKETPLACE","value":"Marketplace"}
  ];

  static EXTENSION_PERIOD_INTERVALS = [
    {"key":1,"value":"1 min"},
    {"key":2,"value":"2 min"},
    {"key":3,"value":"3 min"},
    {"key":4,"value":"4 min"},
    {"key":5,"value":"5 min"},
    {"key":10,"value":"10 min"},
    {"key":15,"value":"15 min"},
    {"key":20,"value":"20 min"},
    {"key":30,"value":"30 min"}
  ];

  static SHOT_CLOCK_EXTENSION_INTERVALS = [
    {"key":0.5,"value":"30 sec"},
    {"key":1,"value":"1 min"},
    {"key":2,"value":"2 min"},
    {"key":3,"value":"3 min"},
    {"key":4,"value":"4 min"},
    {"key":5,"value":"5 min"},
    {"key":10,"value":"10 min"},
    {"key":20,"value":"20 min"},
    {"key":30,"value":"30 min"}
  ];

  static COMMISSION_TYPES = [
    {"key":CommissionType.FLAT_FEE,"value":"Flat fee"},
    {"key":CommissionType.PERCENT_OF_GROSS,"value":"% of gross"}
  ];

  static BANK_ACCOUNT_TYPES = [
    {"key":BankAccountType.CUSTODIAL,"value":"Custodial"},
    {"key":BankAccountType.OPERATING,"value":"Operating"}
  ];

  auctionId:string;
  title:string;
  startTime:string;
  endTime:string;
  locationType:string;
  locationCity:string;
  locationState:string;
  description:string;
  businessAccountId:string;
  type:string;
  commissionType:CommissionType;
  commissionValue:number;
  bankAccountType:BankAccountType;
  closingExtensionEnabled:boolean;
  closingExtensionStyle: string;
  closingExtensionInterval: number;
  closingExtensionTriggered: boolean;
  defaultLotOpenBiddingPeriodMinutes: number;
  activeShotClockLotId: string;
  status:AuctionStatus;
  salesInfoBannerKeys:string[];
  salesInfoNotes:string;
  sellerLocationTitle:string;
  sellerContactName:string;
  sellerAddress: string[];
  sellerCity:string;
  sellerState:string;
  sellerZip:string;
  sellerEmail:string;
  sellerPhoneNumbers:PhoneNumberDetailInput[];
  saleOverview: string[];
  salesRepName:string;
  salesRepContact:string;
  salesRepEmail:string;
  welcomeMessage:string;
  bidIncrement:number;
  startBid:number;
  floorPrice:number;
  catalogKey:string;
  introVideoUrl:string;
  paymentOptions:string[];
  shippingInstructions:string;
  receivingInstructions:string;
  liveBidLocation:string;
  bannerImageKey:string;
  hasCustomInvoiceDetails:boolean;
  hasStandardTermsAndConditions:boolean;
  hasCustomTermsAndConditions:boolean;
  customTermsAndConditions:string;
  hasStandardDeliveryAndPaymentTerms:boolean;
  hasCustomDeliveryAndPaymentTerms:boolean;
  customDeliveryAndPaymentTerms:string;
  timezone:string;
  useSellerContactInfo:boolean;
  delisted: boolean;
  version:number;

  lots:LotModel[] = [];
  businessName:string;

  primaryBusiness:PortalUser;
  businesses:PortalUser[] = [];
  auctioneers:PortalUser[] = [];

  businessAccountIds:string[] = [];
  businessModifiedAccountIds:string[] = [];

  salesInfoBannerUrls:[] = [];
  catalogKeyUrl;
  bannerImageKeyUrl;
  bidders:BidderModel[];

  salemanagementcontactdefault = environment.salemanagementcontactdefault;

  timeStatistics: AuctionTimeStatistics;

  constructor() { }

  load(item:{}){
    this.auctionId = item['auctionId'] != undefined ? item['auctionId'] : "";
    this.title = item['title'] != undefined ? item['title'] : undefined;
    this.startTime = item['startTime'] != undefined ? item['startTime'] : undefined;
    this.endTime = item['endTime'] != undefined ? item['endTime'] : undefined;
    this.locationType = item['locationType'] != undefined ? item['locationType'] : undefined;
    this.locationCity = item['locationCity'] != undefined ? item['locationCity'] : undefined;
    this.locationState = item['locationState'] != undefined ? item['locationState'] : undefined;
    this.description = item['description'] != undefined ? item['description'] : undefined;
    this.businessAccountId = item['businessAccountId'] != undefined ? item['businessAccountId'] : undefined;
    this.type = item['type'] != undefined ? item['type'] : undefined;
    this.commissionType = item['commissionType'] != undefined ? item['commissionType'] : undefined;
    this.commissionValue = item['commissionValue'] != undefined ? item['commissionValue'] : undefined;
    this.bankAccountType = item['bankAccountType'] != undefined ? item['bankAccountType'] : undefined;
    this.closingExtensionEnabled = item['closingExtensionEnabled'] != undefined ? item['closingExtensionEnabled'] : true;
    this.closingExtensionStyle = item['closingExtensionStyle'] != undefined ? item['closingExtensionStyle'] : undefined;
    this.closingExtensionInterval = item['closingExtensionInterval'] != undefined ? item['closingExtensionInterval'] : undefined;
    this.defaultLotOpenBiddingPeriodMinutes = item['defaultLotOpenBiddingPeriodMinutes'] != undefined ? item['defaultLotOpenBiddingPeriodMinutes'] : undefined;
    this.activeShotClockLotId = item['activeShotClockLotId'] != undefined ? item['activeShotClockLotId'] : undefined;
    this.status = item['status'] != undefined ? item['status'] : undefined;
    this.salesInfoBannerKeys = item['salesInfoBannerKeys'] != undefined ? item['salesInfoBannerKeys'] : [];
    this.salesInfoNotes = item['salesInfoNotes'] != undefined ? item['salesInfoNotes'] : undefined;
    this.sellerLocationTitle = item['sellerLocationTitle'] != undefined ? item['sellerLocationTitle'] : undefined;
    this.sellerContactName = item['sellerContactName'] != undefined ? item['sellerContactName'] : undefined;
    this.sellerAddress = item['sellerAddress'] != undefined ? item['sellerAddress'] : [];
    this.sellerCity = item['sellerCity'] != undefined ? item['sellerCity'] : undefined;
    this.sellerState = item['sellerState'] != undefined ? item['sellerState'] : undefined;
    this.sellerZip = item['sellerZip'] != undefined ? item['sellerZip'] : undefined;
    this.sellerEmail = item['sellerEmail'] != undefined ? item['sellerEmail'] : undefined;
    this.sellerPhoneNumbers = item['sellerPhoneNumbers'] != undefined ? item['sellerPhoneNumbers'] : [];
    this.saleOverview = item['saleOverview'] != undefined ? item['saleOverview'] : [];
    this.salesRepContact = item['salesRepContact'] != undefined ? item['salesRepContact'] : undefined;
    this.salesRepName = item['salesRepName'] != undefined ? item['salesRepName'] : undefined;
    this.salesRepEmail = item['salesRepEmail'] != undefined ? item['salesRepEmail'] : undefined;
    this.welcomeMessage = item['welcomeMessage'] != undefined ? item['welcomeMessage'] : undefined;
    this.bidIncrement = item['bidIncrement'] != undefined ? item['bidIncrement'] : undefined;
    this.startBid = item['startBid'] != undefined ? item['startBid'] : undefined;
    this.floorPrice = item['floorPrice'] != undefined ? item['floorPrice'] : undefined;
    this.catalogKey = item['catalogKey'] != undefined ? item['catalogKey'] : undefined;
    this.introVideoUrl = item['introVideoUrl'] != undefined ? item['introVideoUrl'] : undefined;
    this.paymentOptions = item['paymentOptions'] != undefined ? item['paymentOptions'] : undefined;
    this.shippingInstructions = item['shippingInstructions'] != undefined ? item['shippingInstructions'] : undefined;
    this.receivingInstructions = item['receivingInstructions'] != undefined ? item['receivingInstructions'] : undefined;
    this.liveBidLocation = item['liveBidLocation'] != undefined ? item['liveBidLocation'] : undefined;
    this.bannerImageKey = item['bannerImageKey'] != undefined ? item['bannerImageKey'] : undefined;
    this.hasCustomInvoiceDetails = item['hasCustomInvoiceDetails'] != undefined ? item['hasCustomInvoiceDetails'] : false;
    this.hasStandardTermsAndConditions = item['hasStandardTermsAndConditions'] != undefined ? item['hasStandardTermsAndConditions'] : true;
    this.hasCustomTermsAndConditions = item['hasCustomTermsAndConditions'] != undefined ? item['hasCustomTermsAndConditions'] : true;
    this.customTermsAndConditions = item['customTermsAndConditions'] != undefined ? item['customTermsAndConditions'] : true;
    this.hasStandardDeliveryAndPaymentTerms = item['hasStandardDeliveryAndPaymentTerms'] != undefined ? item['hasStandardDeliveryAndPaymentTerms'] : true;
    this.hasCustomDeliveryAndPaymentTerms = item['hasCustomDeliveryAndPaymentTerms'] != undefined ? item['hasCustomDeliveryAndPaymentTerms'] : true;
    this.customDeliveryAndPaymentTerms = item['customDeliveryAndPaymentTerms'] != undefined ? item['customDeliveryAndPaymentTerms'] : true;
    this.timezone = item['timezone'] != undefined ? item['timezone'] : undefined;
    this.useSellerContactInfo = item['useSellerContactInfo'] != undefined ? item['useSellerContactInfo'] : false;
    this.delisted = item['delisted'] != undefined ? item['delisted'] : false;
    this.version = item['version'] != undefined ? item['version'] : undefined;

    var lotsTemp = item['lots'] != undefined ? item['lots']['items'] : [];
    this.lots = [];
    if(lotsTemp != undefined){
      for(let [index,item] of lotsTemp.entries()){
        let lot = new LotModel().load(item);
        this.lots.push(lot);
      }
      this.lots = this.lots.sort(this.compareLotOrder);
    }

    if(item['primaryBusiness'] != undefined){
      this.primaryBusiness = new PortalUser();
      this.primaryBusiness.loadResults(item['primaryBusiness']);
      this.businessName = this.primaryBusiness.businessName != undefined ? this.primaryBusiness.businessName : "";
    }
    else {
      this.businessName = "";
    }

    if(item['businesses'] != undefined){
      this.businesses = [];
      this.businessAccountIds = [];
      this.businessModifiedAccountIds = [];
      for(let value of item['businesses']){
        let business = new PortalUser();
        business.loadResults(value)
        this.businesses.push(business);
        this.businessAccountIds.push(value.accountId);
        this.businessModifiedAccountIds.push(value.accountId);
      }
    }

    if(item['auctioneers'] != undefined){
      this.auctioneers = [];
      for(let value of item['auctioneers']){
        let auctioneer = new PortalUser();
        auctioneer.loadResults(value);
        this.auctioneers.push(auctioneer);
      }
    }

    if (this.startTime && this.endTime) {
      this.refreshTimeStatistics();
    }

    return this;
  }

  async loadBidders(){
    let queryVars = {};
    queryVars['auctionId'] = this.auctionId;
    try{
      const response = (await API.graphql(graphqlOperation(auctionBiddersCustom,queryVars))) as {
        data: AuctionBiddersCustomQuery;
      };
      // console.log('get auction bidders',response.data);
      let item = response.data.auction;

      this.bidders = [];
      if(item['bidders'] != undefined && item['bidders']['items'] != undefined && item['bidders']['items'].length > 0){
        for(let bidder of item['bidders']['items']){
          let bidderModel = new BidderModel();
          bidderModel.load(bidder);
          this.bidders.push(bidderModel);
        }
      }

    }
    catch(err){
      console.log('error loading auction bidders',err);
      throw(err)
    }
    return this;
  }

  compareLotOrder( a:LotModel, b:LotModel ) {
    let valueA:number = a.lotOrder;
    let valueB:number = b.lotOrder;
    return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
  }

  async get(auctionId:string){
    let queryVars = {};
    queryVars['auctionId'] = auctionId;
    try{
      let response = (await API.graphql(graphqlOperation(auctionCustom,queryVars))) as {
        data: AuctionQuery;
      };
      // console.log('get auction',response.data);
      this.load(response.data.auction);

      var lotsContinueToken = response.data.auction['lots'] != undefined ? response.data.auction['lots']['nextToken'] : undefined;
      var continueLoop = lotsContinueToken != undefined;

      while(continueLoop){
        queryVars = {};
        queryVars['auctionId'] = auctionId;
        queryVars['lotNextToken'] = lotsContinueToken;

        response = (await API.graphql(graphqlOperation(auctionCustom,queryVars))) as {
          data: AuctionQuery;
        };
        // console.log('get more lots from auction',response.data);
        var lotsTemp = [];
        if(response.data.auction['lots'] != undefined &&  response.data.auction['lots']['items'] != undefined){
          lotsTemp =response.data.auction['lots']['items'];
        }

        if(lotsTemp != undefined){
          for(let [index,item] of lotsTemp.entries()){
            let lot = new LotModel().load(item);
            this.lots.push(lot);
          }
          this.lots = this.lots.sort(this.compareLotOrder);
        }

        lotsContinueToken = response.data.auction['lots'] != undefined ? response.data.auction['lots']['nextToken'] : undefined;
        continueLoop = lotsContinueToken != undefined;

      }
    }
    catch(err){
      console.log('error loading auction',err);
      throw(err)
    }
    return this;
  }

  async getAllLotsForBidsActivity(auctionId:string){
    let queryVars = {};
    queryVars['auctionId'] = auctionId;
    try{
      let response = (await API.graphql(graphqlOperation(auctionAllLotBidsCustom,queryVars))) as {
        data: AuctionAllLotBidsCustomQuery;
      };
      // console.log('get auction',response.data);
      this.load(response.data.auction);

      var lotsContinueToken = response.data.auction['lots'] != undefined ? response.data.auction['lots']['nextToken'] : undefined;
      var continueLoop = lotsContinueToken != undefined;

      while(continueLoop){
        queryVars = {};
        queryVars['auctionId'] = auctionId;
        queryVars['lotNextToken'] = lotsContinueToken;

        response = (await API.graphql(graphqlOperation(auctionAllLotBidsCustom,queryVars))) as {
          data: AuctionAllLotBidsCustomQuery;
        };
        // console.log('get more lots from auction',response.data);
        var lotsTemp = [];
        if(response.data.auction['lots'] != undefined &&  response.data.auction['lots']['items'] != undefined){
          lotsTemp =response.data.auction['lots']['items'];
        }

        if(lotsTemp != undefined){
          for(let [index,item] of lotsTemp.entries()){
            let lot = new LotModel().load(item);
            this.lots.push(lot);
          }
          this.lots = this.lots.sort(this.compareLotOrder);
        }

        lotsContinueToken = response.data.auction['lots'] != undefined ? response.data.auction['lots']['nextToken'] : undefined;
        continueLoop = lotsContinueToken != undefined;

      }
    }
    catch(err){
      console.log('error loading auction',err);
      throw(err)
    }
    return this;
  }

  async updateStatus(status:AuctionStatus){
    if(this.auctionId == undefined || this.auctionId.length == 0){
      throw("Auction not yet saved.");
    }
    if(status == AuctionStatus.SETTLED && !this.hasSettlementAccountingData()){
      throw("Commission type, a positive commission value, and bank account type are required before settlement.");
    }
    try{
      let updateVars = {};
      updateVars['status'] = status;
      if(status == AuctionStatus.SETTLED){
        updateVars['commissionType'] = this.commissionType;
        updateVars['commissionValue'] = this.commissionValue;
        updateVars['bankAccountType'] = this.bankAccountType;
      }
      const response = (await API.graphql(graphqlOperation(updateAuction,{auctionId:this.auctionId,expectedVersion:this.version,input:updateVars}))) as {
        data: UpdateAuctionMutation;
      };

      if(response.data == undefined || response.data.updateAuction == undefined){
        throw new Error('Error updating auction status. Contact your administrator.');
      }
      // console.log('update auction',response);
      return await this.get(this.auctionId);
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error updating auction',err);
      throw(errorMsg)
    }
  }

  async updateClosingExtensionInterval(){
    if(this.auctionId == undefined || this.auctionId.length == 0){
      throw("Auction not yet saved.");
    }
    try{
      let updateVars = {};
      updateVars['closingExtensionInterval'] = this.closingExtensionInterval;
      const response = (await API.graphql(graphqlOperation(updateAuction,{auctionId:this.auctionId,expectedVersion:this.version,input:updateVars}))) as {
        data: UpdateAuctionMutation;
      };

      if(response.data == undefined || response.data.updateAuction == undefined){
        throw new Error('Error updating auction extension interval. Contact your administrator.');
      }
      return await this.get(this.auctionId);
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error updating auction',err);
      throw(errorMsg)
    }
  }

  async update(){
    if(this.auctionId == undefined || this.auctionId.length == 0){
      return this.create();
    }
    try{
      let updateVars = this.getUpdateVars();
      const response = (await API.graphql(graphqlOperation(updateAuction,{auctionId:this.auctionId,expectedVersion:this.version,input:this.getUpdateVars()}))) as {
        data: UpdateAuctionMutation;
      };
      // console.log('update auction',response);
      if(response.data == undefined || response.data.updateAuction == undefined){
        throw new Error('Error updating auction. Contact your administrator.');
      }
      await this.saveBusinesses();
      return await this.get(this.auctionId);
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error updating auction',err);
      throw(errorMsg)
    }
  }

  async create(){
    // console.log(this.getUpdateVars());
    try{
      const response = (await API.graphql(graphqlOperation(createAuction,{input:this.getUpdateVars()}))) as {
        data: CreateAuctionMutation;
      };

      if(response.data == undefined || response.data.createAuction == undefined){
        throw new Error('Error creating auction. Contact your administrator.');
      }
      // console.log('create auction',response);
      this.auctionId = response.data.createAuction['auctionId'];
      await this.saveBusinesses();
      await this.get(this.auctionId);
      return this;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error creating auction',err);
      throw(errorMsg)
    }
  }

  async delete(){
    try{
      await this.deleteBusinesses();
      const response = (await API.graphql(graphqlOperation(deleteAuction,{auctionId:this.auctionId}))) as {
        data: DeleteAuctionMutation;
      };

      if(response.data == undefined || response.data.deleteAuction == undefined){
        throw new Error('Error deleting auction. Contact your administrator.');
      }
      return;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error deleting auction',err);
      throw(errorMsg)
    }
  }

  async saveBusinesses(){

    /* Add businesses that do not exist*/
    for(let item of this.businessModifiedAccountIds){
      if(!this.businessAccountIds.includes(item)){
        await this.addBusiness(item);
      }
    }

    /* Delete businesses */
    for(let item of this.businessAccountIds){
      if(!this.businessModifiedAccountIds.includes(item)){
        await this.deleteBusiness(item);
      }
    }
  }

  async deleteBusinesses(){
    for(let item of this.businessAccountIds){
        await this.deleteBusiness(item);
    }
  }

  async addBusiness(businessAccountId:string){
    try{
      const response = (await API.graphql(graphqlOperation(addAuctionBusiness,{auctionId:this.auctionId,businessAccountId:businessAccountId}))) as {
        data: AddAuctionBusinessMutation;
      };

      if(response.data == undefined || response.data.addAuctionBusiness == undefined){
        throw new Error('Error adding auction business. Contact your administrator.');
      }
      return;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error adding auction business',err);
      throw(errorMsg)
    }
  }

  async deleteBusiness(businessAccountId:string){
    try{
      const response = (await API.graphql(graphqlOperation(deleteAuctionBusiness,{auctionId:this.auctionId, businessAccountId:businessAccountId}))) as {
        data: DeleteAuctionBusinessMutation;
      };

      if(response.data == undefined || response.data.deleteAuctionBusiness == undefined){
        throw new Error('Error deleting auction business. Contact your administrator.');
      }
      return;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error deleting auction business',err);
      throw(errorMsg)
    }
  }

  async addAuctioneer(accountId:string){
    try{
      const response = (await API.graphql(graphqlOperation(addAuctionAuctioneer,{auctionId:this.auctionId, accountId:accountId}))) as {
        data: any;
      };
      const result = response.data && response.data.addAuctionAuctioneer;
      if(result == undefined || result.status == "REJECTED"){
        throw new Error((result && result.message) || 'Error adding auction auctioneer. Contact your administrator.');
      }
      return result;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error adding auction auctioneer',err);
      throw(errorMsg)
    }
  }

  async deleteAuctioneer(accountId:string){
    try{
      const response = (await API.graphql(graphqlOperation(deleteAuctionAuctioneer,{auctionId:this.auctionId, accountId:accountId}))) as {
        data: any;
      };
      const result = response.data && response.data.deleteAuctionAuctioneer;
      if(result == undefined || result.status == "REJECTED"){
        throw new Error((result && result.message) || 'Error deleting auction auctioneer. Contact your administrator.');
      }
      return result;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error deleting auction auctioneer',err);
      throw(errorMsg)
    }
  }

  async deleteAuctionAndAssets(){
    try{
      const response = (await API.graphql(graphqlOperation(deleteAuctionAndAssets,{auctionId:this.auctionId}))) as {
        data: DeleteAuctionAndAssetsMutation;
      };

      if(response.data == undefined || response.data.deleteAuctionAndAssets == undefined){
        throw new Error('Error deleting auction auction and assets. Contact your administrator.');
      }
      // console.log('deleteAuctionAndAssets',response);
      let deletedAuctionId = response.data.deleteAuctionAndAssets['auctionId'];
      return deletedAuctionId;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error deleting auction and assets',err);
      throw(errorMsg)
    }
  }

  async cloneAuctionAndAssets(auctionId:string){
    try{
      const response = (await API.graphql(graphqlOperation(cloneAuctionAndAssets,{auctionId:auctionId}))) as {
        data: CloneAuctionAndAssetsMutation;
      };

      if(response.data == undefined || response.data.cloneAuctionAndAssets == undefined){
        throw new Error('Error cloning auction auction and assets. Contact your administrator.');
      }
      // console.log('cloneAuctionAndAssets',response);
      let clonedAuctionId = response.data.cloneAuctionAndAssets['auctionId'];
      return clonedAuctionId;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error cloning auction and assets',err);
      throw(errorMsg)
    }
  }

  async updateLotOrder(){
    try{
      // console.log(this.getUpdateVars());
      let lotsToUpdate = [];
      for(let item of this.lots){
        let lotItem = {
          "auctionId":this.auctionId,
          "lotId":item.lotId,
          "lotOrder":item.lotOrder
        }
        lotsToUpdate.push(lotItem);
      }

      const response = (await API.graphql(graphqlOperation(updateLotOrder,{auctionId:this.auctionId,lots:lotsToUpdate}))) as {
        data: UpdateLotOrderMutation;
      };

      // console.log('UpdateLotOrderMutation',response);
      if(response.data == undefined || response.data.updateLotOrder == undefined){
        throw new Error('Error updating auction lot order. Contact your administrator.');
      }
      return await this.get(this.auctionId);
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error updating auction lot order',err);
      throw(errorMsg)
    }
  }

  getUpdateVars(){
    var returnValue = {};
    returnValue['title'] = this.title;
    returnValue['startTime'] = this.startTime;
    returnValue['endTime'] = this.endTime;
    returnValue['locationType'] = this.locationType;
    returnValue['locationCity'] = this.locationCity;
    returnValue['locationState'] = this.locationState;
    returnValue['description'] = this.description;
    returnValue['businessAccountId'] = this.businessAccountId;
    returnValue['type'] = this.type;
    returnValue['commissionType'] = this.commissionType;
    returnValue['commissionValue'] = this.commissionValue;
    returnValue['bankAccountType'] = this.bankAccountType;
    returnValue['closingExtensionEnabled'] = this.closingExtensionEnabled;
    returnValue['closingExtensionStyle'] = this.closingExtensionStyle;
    returnValue['closingExtensionInterval'] = this.closingExtensionInterval;
    returnValue['defaultLotOpenBiddingPeriodMinutes'] = this.defaultLotOpenBiddingPeriodMinutes;
    returnValue['activeShotClockLotId'] = this.activeShotClockLotId;
    returnValue['status'] = this.status;
    returnValue['salesInfoBannerKeys'] = this.salesInfoBannerKeys;
    returnValue['salesInfoNotes'] = this.salesInfoNotes;
    returnValue['sellerLocationTitle'] = this.sellerLocationTitle;
    returnValue['sellerContactName'] = this.sellerContactName;
    returnValue['sellerAddress'] = this.sellerAddress;
    returnValue['sellerCity'] = this.sellerCity;
    returnValue['sellerState'] = this.sellerState;
    returnValue['sellerZip'] = this.sellerZip;
    returnValue['sellerEmail'] = this.sellerEmail;
    returnValue['sellerPhoneNumbers'] = this.sellerPhoneNumbers;
    returnValue['saleOverview'] = this.saleOverview;
    returnValue['salesRepName'] = this.salesRepName;
    returnValue['salesRepContact'] = this.salesRepContact != undefined && this.salesRepContact.trim().length > 0 ? this.salesRepContact : null;
    returnValue['salesRepEmail'] = this.salesRepEmail != undefined && this.salesRepEmail.trim().length > 0 ? this.salesRepEmail : null;
    returnValue['welcomeMessage'] = this.welcomeMessage;
    returnValue['bidIncrement'] = this.bidIncrement;
    returnValue['startBid'] = this.startBid;
    returnValue['floorPrice'] = this.floorPrice;
    returnValue['catalogKey'] = this.catalogKey;
    returnValue['introVideoUrl'] = this.introVideoUrl;
    returnValue['paymentOptions'] = this.paymentOptions;
    returnValue['shippingInstructions'] = this.shippingInstructions;
    returnValue['receivingInstructions'] = this.receivingInstructions;
    returnValue['liveBidLocation'] = this.liveBidLocation;
    returnValue['bannerImageKey'] = this.bannerImageKey;
    returnValue['hasCustomInvoiceDetails'] = this.hasCustomInvoiceDetails;
    returnValue['hasStandardTermsAndConditions'] = this.hasStandardTermsAndConditions;
    returnValue['hasCustomTermsAndConditions'] = this.hasCustomTermsAndConditions;
    returnValue['customTermsAndConditions'] = this.customTermsAndConditions;
    returnValue['hasStandardDeliveryAndPaymentTerms'] = this.hasStandardDeliveryAndPaymentTerms;
    returnValue['hasCustomDeliveryAndPaymentTerms'] = this.hasCustomDeliveryAndPaymentTerms;
    returnValue['customDeliveryAndPaymentTerms'] = this.customDeliveryAndPaymentTerms;
    returnValue['timezone'] = this.timezone;
    returnValue['useSellerContactInfo'] = this.useSellerContactInfo;
    return returnValue;
  }

  hasSettlementAccountingData(){
    return !!this.commissionType && this.commissionValue > 0 && !!this.bankAccountType;
  }

  getLogoUrl(){
    return "https://" + environment.image_path_url + "/" + this.auctionId + "/" + this.bannerImageKey;
  }

  getSalesLogoUrl(imageKey){
    return "https://" + environment.image_path_url + "/" + this.auctionId + "/" + imageKey;
  }

  getCatalogUrl(){
    return "https://" + environment.image_path_url + "/" + this.auctionId + "/" + this.catalogKey;
  }

  getPreviewUrl(){
    return "https://" + environment.buyer_portal_path_url + "/previewauction/" + this.auctionId;
  }

  getStandardTermsAndConditionsUrl(){
    return "https://" + environment.buyer_portal_path_url + "/buyers#terms-and-conditions";
  }

  getStandardDeliverAndTermsUrl(){
    return "https://" + environment.buyer_portal_path_url + "/buyers#delivery-payment";
  }

  getSalesLocation(){
    let city = this.sellerCity != undefined ? this.sellerCity + ", " : "";
    let state = this.sellerState != undefined ? this.sellerState + " " : "";
    let zip = this.sellerZip != undefined ? this.sellerZip : "";
    return (city + state + zip);
  }

  getLocation(){
    var returnValue = "";
    if(this.locationType == AuctionLocationType.ONLINE_ONLY){
      returnValue = "Online Only";
    }
    else{
      let city = this.locationCity != undefined ? this.locationCity + ", " : "";
      let state = this.locationState != undefined ? this.locationState : "";
      returnValue = city + state;
    }
    return returnValue;
  }

  isSetup(){
    return this.status == AuctionStatus.SETUP;
  }

  isCalendarPosted(){
    return this.status == AuctionStatus.CALENDAR_POSTED;
  }

  isSaleInfoPosted(){
    return this.status == AuctionStatus.SALE_INFO_POSTED;
  }

  isLotsPosted(){
    return this.status == AuctionStatus.LOTS_POSTED;
  }

  isShotClock(){
    return this.type == "SHOT_CLOCK";
  }

  get activeShotClockLot(){
    if (!this.activeShotClockLotId || !this.lots) return undefined;
    return this.lots.find((lot) => lot.lotId == this.activeShotClockLotId);
  }

  get clockEndTime(){
    if (this.isShotClock() && this.isLive() && this.activeShotClockLot && this.activeShotClockLot.endTime) {
      return this.activeShotClockLot.endTime;
    }
    return this.endTime;
  }

  refreshTimeStatistics(){
    if (this.startTime && this.endTime) {
      this.timeStatistics = new AuctionTimeStatistics(this);
    }
  }

  applyLotSubscriptionData(lotData){
    if (!lotData || !lotData.lotId || !this.lots) {
      return;
    }
    const lot = this.lots.find((candidate) => candidate.lotId == lotData.lotId);
    if (!lot) {
      return;
    }
    // Loose != undefined, matching LotModel.load: AppSync sends explicit null for
    // an absent lot time, which must not blank a clock we already know about.
    if (lotData.startTime != undefined) {
      lot.startTime = lotData.startTime;
    }
    if (lotData.endTime != undefined) {
      lot.endTime = lotData.endTime;
    }
    this.refreshTimeStatistics();
  }

  applyAuctionSubscriptionData(auctionData){
    if (!auctionData) {
      return;
    }
    Object.keys(auctionData).forEach(key => {
      if (key === 'activeShotClockLotId' && (auctionData[key] == null || auctionData[key] === undefined)) {
        const incomingStatus = auctionData.status != null ? auctionData.status : this.status;
        if (incomingStatus == AuctionStatus.LIVE) {
          return;
        }
      }
      this[key] = auctionData[key];
    });
    this.refreshTimeStatistics();
  }

  isLive(){
    return this.status == AuctionStatus.LIVE;
  }

  isClosed(){
    return this.status == AuctionStatus.CLOSED;
  }

  isSettled(){
    return this.status == AuctionStatus.SETTLED;
  }

  isArchived(){
    return this.status == AuctionStatus.ARCHIVED;
  }

  async getFileURL(filename:string,publicAccess:boolean=true){
    try{
      const response = (await API.graphql(graphqlOperation(manageFile,{itemId:this.auctionId,itemKey:filename,public:publicAccess}))) as {
        data: ManageFileMutation;
      };
      // console.log("manageFile:",response);
      return response.data.manageFile;
    }
    catch(err){
      console.log('error getting file url',err);
      throw(err)
    }
  }

  async processUploadedLotsCsv(filename:string){
    var requestVars = {};
    requestVars["taskKey"] = "importLots";
    requestVars["payload"] = JSON.stringify({
      "auctionId":this.auctionId,
      "fileName":filename
    });

    try{
      const response = (await API.graphql(graphqlOperation(initiateTask,requestVars))) as {
        data: InitiateTaskMutation;
      };
      // console.log("initiateTask:",response);
      return response.data.initiateTask;
    }
    catch(err){
      console.log('error initiating importLots task',err);
      throw(err)
    }
  }

  initNewAuction(){
    this.status = AuctionStatus.SETUP;
    this.hasStandardTermsAndConditions = true;
    this.hasStandardDeliveryAndPaymentTerms = true;
    this.hasCustomTermsAndConditions = false;
    this.hasCustomDeliveryAndPaymentTerms = false;
    this.useSellerContactInfo = true;
    this.customDeliveryAndPaymentTerms = "";
    this.customTermsAndConditions = "";
    this.type = "HORSE_STYLE"
    this.closingExtensionEnabled = true;
    this.closingExtensionInterval = 5;
    this.defaultLotOpenBiddingPeriodMinutes = 5;
    let phone = {"phone":"","preferred":true,"mobile":false};
    this.sellerPhoneNumbers = [];
    this.sellerPhoneNumbers.push(phone);
    this.salesRepName = this.salemanagementcontactdefault['name'];
    this.salesRepEmail = this.salemanagementcontactdefault['email'];
    this.salesRepContact = this.salemanagementcontactdefault['phone'];
  }

  async generateBidderReport(auctionId:string){
    try{
      const response = (await API.graphql(graphqlOperation(bidderReport,{auctionId:auctionId}))) as {
        data: BidderReportQuery;
      };
      // console.log('bidderReport response',response.data.bidderReport);
      return response.data.bidderReport;
    }
    catch(err){
      console.log('error generating bidderReport',err);
      throw(err)
    }
  }


}

export class LotModel {

  static WINNING_BID_STR               = "YOU HAVE THE WINNING BID";
  static OUT_BID_STR                   = "YOU HAVE BEEN OUTBID";
  static BID_UNSUCESSFUL_STR           = "YOUR BID WAS UNSUCCESSFUL";
  static AUCTION_LOT_CLOSED_STR        = "BIDDING HAS CLOSED FOR LOT";
  static NOT_AUTHORIZED_TO_BID_STR     = "NOT AUTHORIZED";

  static LOT_CATEGORIES = [
    {"key":AuctionLotCategory.COMMERCIAL,"value":"Commercial"},
    {"key":AuctionLotCategory.EMBRYO,"value":"Embryo"},
    {"key":AuctionLotCategory.FLUSH,"value":"Flush"},
    {"key":AuctionLotCategory.REGISTERED_BULL,"value":"Registered Bull"},
    {"key":AuctionLotCategory.REGISTERED_COW,"value":"Registered Cow"},
    {"key":AuctionLotCategory.REGISTERED_HEIFER,"value":"Registered Heifer"},
    {"key":AuctionLotCategory.PREGNANCY,"value":"Pregnancy"},
    {"key":AuctionLotCategory.SEMEN,"value":"Semen"},
    {"key":AuctionLotCategory.OTHER,"value":"Other"}
  ];

  static SEXED_CATEGORIES = [
    {"key":SexedCategory.BULL,"value":"Bull"},
    {"key":SexedCategory.COW,"value":"Cow"},
    {"key":SexedCategory.HEIFER,"value":"Heifer"},
    {"key":SexedCategory.SEXED,"value":"Sexed"},
    {"key":SexedCategory.UNSEXED,"value":"Unsexed"},
    {"key":SexedCategory.CONFIRMED_HEIFER,"value":"Confirmed Heifer"},
    {"key":SexedCategory.MALE,"value":"Steer"},
    {"key":SexedCategory.FEMALE,"value":"Female"}
  ];

  static INTEREST_CATEGORIES = [
    // {"key":InterestType.WHOLE,"value":"Whole"},
    {"key":InterestType.THREE_FOURTHS,"value":"3/4"},
    {"key":InterestType.TWO_THIRDS,"value":"2/3"},
    {"key":InterestType.ONE_HALF,"value":"1/2"},
    {"key":InterestType.ONE_THIRD,"value":"1/3"},
    {"key":InterestType.ONE_FOURTH,"value":"1/4"}
  ];

  // static EMBRYO_TYPES = [
  //   {"key":LotEmbryoType.CONVENTIONAL,"value":"Conventional"},
  //   {"key":LotEmbryoType.IVF,"value":"IVF"}
  // ];

  static EXPORTABLE_TYPES = [
    {"key":ExportableType.Y,"value":"Y"},
    {"key":ExportableType.USA_ONLY,"value":"USA Only"}
  ];

  static EPDS_PRODUCTION = ["CED","BW","WW","YW","RADG","DMI","YH","SC"];
  static EPDS_MATERNAL = ["HP","CEM","MILK","MKH","MW","MH","$EN"];
  static EPDS_MANAGEMENT = ["DOC","CLAW","ANGLE","PAP"];
  static EPDS_WEIGHT = ["BW","200DW","400DW","600DW","WW","YW","MW"];
  static EPDS_FERTILITY = ["GL","MILK","TOTMAT","SS"];
  static EPDS_CARCASS = ["CW","EMA","RUMP","RIB","RBY","MARB","MARBF","RE","FAT","C","U"];
  static EPDS_$VALUES = ["$BFI","$SRI","$FTI","$F1I","$M","$W","$F","$G","$B","$C"];

  /** Theme-aware: Wagyu uses 200MAT instead of TOTMAT. */
  static getEPDS_FERTILITY(): string[] {
    const t = (environment.theme_config as { epd_fertility_measures?: string[] })?.epd_fertility_measures;
    return t && t.length === LotModel.EPDS_FERTILITY.length ? t : LotModel.EPDS_FERTILITY;
  }
  /** Theme-aware: Wagyu uses $BFI instead of $SRI in the same slot. */
  static getEPDS_$VALUES(): string[] {
    const t = (environment.theme_config as { epd_$values_measures?: string[] })?.epd_$values_measures;
    return t && t.length === LotModel.EPDS_$VALUES.length ? t : LotModel.EPDS_$VALUES;
  }

  auctionId:string;
  lotId:string;
  title:string;
  lotOrder:number;
  biddingDisabled:boolean;
  openBiddingPeriodMinutes:number;
  startTime:string;
  endTime:string;
  endTimeTaskId:string;
  customFields: CustomLotField[];
  lotNum:string;
  category:AuctionLotCategory;
  donorName:string;
  donorSire:string;
  donorDam:string;
  breed:string;
  consigner:string;
  specialTerms:string;
  deliveryMethods:[];
  startBid:number;
  floorPrice:number;
  interest:InterestType;
  embryoType:string;
  quantity:number;
  taxable:boolean;
  tag:string;
  regNum:string;
  regUrl:string;
  lineageHierarchy:LineageHierarchy;
  epdMeasures: EPDMeasures[];
  tattoo:string;
  dob:string;
  sexedCategory:SexedCategory;
  recipientId:string;
  exportable:ExportableType;
  footnote: string[];
  contactInfo:string;
  pictures:boolean;
  photos:LotImage[];
  videos:LotImage[];
  externalVideoUrl:string;
  bidIncrement:number;
  commissionType:CommissionType | null;
  commissionValue:number | null;
  currentBid:number;
  nextBid:number;
  // bannerImageKey:string;
  businessAccountId:string;
  lastBidTime:string;
  version:number;
  bidCount:number;
  bidderAccountId:string;
  bidderId:string;
  bidderCount:number;
  actualEPD:boolean;
  importError:boolean;
  importErrors:string[];
  bids:BidModel[];

  status: BidResultStatus;
  code: BidResultCode;
  message: string = "";
  isNewBidderId:boolean;

  bidsPagingTokens;
  currentBiddingPage:number = undefined;

  winMsg:string = "";
  outBidMsg:string = "";

  constructor() { }

  load(item:{}){
    this.auctionId = item['auctionId'] != undefined ? item['auctionId'] : undefined;
    this.lotId = item['lotId'] != undefined ? item['lotId'] : undefined;
    this.lotNum = item['lotNum'] != undefined ? item['lotNum'] : undefined;
    this.title = item['title'] != undefined ? item['title'] : undefined;
    this.lotOrder = item['lotOrder'] != undefined ? item['lotOrder'] : undefined;
    this.biddingDisabled = item['biddingDisabled'] != undefined ? item['biddingDisabled'] : false;
    this.openBiddingPeriodMinutes = item['openBiddingPeriodMinutes'] != undefined ? item['openBiddingPeriodMinutes'] : undefined;
    this.startTime = item['startTime'] != undefined ? item['startTime'] : undefined;
    this.endTime = item['endTime'] != undefined ? item['endTime'] : undefined;
    this.endTimeTaskId = item['endTimeTaskId'] != undefined ? item['endTimeTaskId'] : undefined;
    this.customFields = item['customFields'] != undefined ? item['customFields'] : undefined;
    this.category = item['category'] != undefined ? item['category'] : undefined;
    this.donorName = item['donorName'] != undefined ? item['donorName'] : undefined;
    this.donorSire = item['donorSire'] != undefined ? item['donorSire'] : undefined;
    this.donorDam = item['donorDam'] != undefined ? item['donorDam'] : undefined;
    this.breed = item['breed'] != undefined ? item['breed'] : undefined;
    this.consigner = item['consigner'] != undefined ? item['consigner'] : undefined;
    this.specialTerms = item['specialTerms'] != undefined ? item['specialTerms'] : undefined;
    this.deliveryMethods = item['deliveryMethods'] != undefined ? item['deliveryMethods'] : undefined;
    this.startBid = item['startBid'] != undefined ? item['startBid'] : undefined;
    this.floorPrice = item['floorPrice'] != undefined ? item['floorPrice'] : undefined;
    this.interest = item['interest'] != undefined ? item['interest'] : undefined;
    this.embryoType = item['embryoType'] != undefined ? item['embryoType'] : undefined;
    this.quantity = item['quantity'] != undefined ? item['quantity'] : undefined;
    this.taxable = item['taxable'] != undefined ? item['taxable'] : undefined;
    this.tag = item['tag'] != undefined ? item['tag'] : undefined;
    this.regNum = item['regNum'] != undefined ? item['regNum'] : undefined;
    this.regUrl = item['regUrl'] != undefined ? item['regUrl'] : undefined;

    this.lineageHierarchy = item['lineageHierarchy'] != undefined ? item['lineageHierarchy'] : this.getInitializedLineage();
    this.epdMeasures = item['epdMeasures'] != undefined ? item['epdMeasures'] : this.getInitializedEPDs();

    this.tattoo = item['tattoo'] != undefined ? item['tattoo'] : undefined;
    this.dob = item['dob'] != undefined ? item['dob'] : undefined;
    this.sexedCategory = item['sexedCategory'] != undefined ? item['sexedCategory'] : undefined;
    this.recipientId = item['recipientId'] != undefined ? item['recipientId'] : undefined;
    this.exportable = item['exportable'] != undefined ? item['exportable'] : undefined;
    this.footnote = item['footnote'] != undefined ? item['footnote'] : undefined;
    this.contactInfo = item['contactInfo'] != undefined ? item['contactInfo'] : undefined;
    this.pictures = item['pictures'] != undefined ? item['pictures'] : undefined;
    this.photos = item['photos'] != undefined ? item['photos'] : undefined;
    this.videos = item['videos'] != undefined ? item['videos'] : undefined;
    this.externalVideoUrl = item['externalVideoUrl'] != undefined ? item['externalVideoUrl'] : undefined;
    this.bidIncrement = item['bidIncrement'] != undefined ? item['bidIncrement'] : undefined;
    this.commissionType = item['commissionType'] != undefined ? item['commissionType'] : null;
    this.commissionValue = item['commissionValue'] != undefined ? item['commissionValue'] : null;
    this.currentBid = item['currentBid'] != undefined ? item['currentBid'] : undefined;
    this.nextBid = item['nextBid'] != undefined ? item['nextBid'] : undefined;
    // this.bannerImageKey = item['bannerImageKey'] != undefined ? item['bannerImageKey'] : undefined;
    this.businessAccountId = item['businessAccountId'] != undefined ? item['businessAccountId'] : undefined;
    this.bidCount = item['bidCount'] != undefined ? item['bidCount'] : undefined;
    this.bidderAccountId = item['bidderAccountId'] != undefined ? item['bidderAccountId'] : undefined;
    this.bidderId = item['bidderId'] != undefined ? item['bidderId'] : undefined;
    this.bidderCount = item['bidderCount'] != undefined ? item['bidderCount'] : undefined;
    this.actualEPD = item['actualEPD'] != undefined ? item['actualEPD'] : true;
    this.importError = item['importError'] != undefined ? item['importError'] : false;
    this.importErrors = item['importErrors'] != undefined ? item['importErrors'] : undefined;
    this.lastBidTime = item['lastBidTime'] != undefined ? item['lastBidTime'] : undefined;
    this.version = item['version'] != undefined ? item['version'] : undefined;

    this.bids = [];
    if( item['bids'] != undefined && item['bids']['items'] != undefined && item['bids']['items'].length > 0){
      for(let bid of item['bids']['items']){
        var bidModel = new BidModel();
        bidModel.load(bid);
        this.bids.push(bidModel);
      }
    }

    this.bidsPagingTokens = [];
    if( item['bids'] != undefined){
      this.currentBiddingPage = 0;
      let pagingTokens = {
        "page":0,
        "prev":undefined,
        "current":undefined,
        "next":item['bids']['nextToken']
      };
      this.bidsPagingTokens.push(pagingTokens);
    }

    return this;
  }

  async getNextBids(bidLimit:number=10){

    if(this.currentBiddingPage == undefined){
      this.bidsPagingTokens = [];
      this.currentBiddingPage = 0;
      let nextToken = await this.pageBids(bidLimit,undefined);
      let pagingTokens = {"page":this.currentBiddingPage,"prev":undefined,"current":undefined ,"next":nextToken };
      this.bidsPagingTokens.push(pagingTokens);
    }
    else if(this.moreBidsAvailable()){
      let current = this.getBiddingPage(this.currentBiddingPage);
      let next = this.getBiddingPage(this.currentBiddingPage+1);
      let nextToken = await this.pageBids(bidLimit,current['next']);
      this.currentBiddingPage = this.currentBiddingPage + 1;
      if(next == undefined){
        let pagingTokens = {"page":this.currentBiddingPage,"prev":current['current'],"current":current['next'] ,"next":nextToken };
        this.bidsPagingTokens.push(pagingTokens);
      }
    }
  }

  async getPreviousBids(bidLimit:number=10){
    if(this.previousBidsAvailable()){
      this.currentBiddingPage = this.currentBiddingPage - 1;
      let current = this.getBiddingPage(this.currentBiddingPage);
      let nextToken = await this.pageBids(bidLimit,current['current']);
    }
  }

  getBiddingPage(biddingPage){
    return this.bidsPagingTokens.find((el)=>{return el['page'] == biddingPage})
  }

  async pageBids(bidLimit,bidsToken:string) {
    let queryVars = {};
    queryVars['auctionId'] = this.auctionId;
    queryVars['lotId'] = this.lotId;
    queryVars['bidLimit'] = bidLimit;
    bidsToken != undefined ? queryVars['bidNextToken'] = bidsToken : "";

    try{
      const response = (await API.graphql(graphqlOperation(auctionLotBidsCustom,queryVars))) as {
        data: AuctionLotBidsCustomQuery;
      };
      // console.log('AuctionLotBidsCustomQuery',response.data);
      var item = response.data.auctionLot;

      this.bids = [];
      if( item['bids']['items'] != undefined && item['bids']['items'].length > 0){
        for(let bid of item['bids']['items']){
          var bidModel = new BidModel();
          bidModel.parentLotId = this.lotId;
          bidModel.load(bid);
          this.bids.push(bidModel);
        }
      }

      return item['bids']['nextToken'];

    }
    catch(err){
      console.log('error retrieving lot',err);
      throw(err)
    }
    return this;
  }

  moreBidsAvailable(){
    let biddingPage = this.getBiddingPage(this.currentBiddingPage);
    return (biddingPage != undefined && biddingPage['next'] != undefined);
  }

  previousBidsAvailable(){
    return this.currentBiddingPage > 0;
  }

  async get(auctionId:string, lotId:string) {
    let queryVars = {};
    queryVars['auctionId'] = auctionId;
    queryVars['lotId'] = lotId;

    try{
      const response = (await API.graphql(graphqlOperation(auctionLotCustom,queryVars))) as {
        data: AuctionLotCustomQuery;
      };
      // console.log('get lot',response.data);
      this.load(response.data.auctionLot);
    }
    catch(err){
      console.log('error retrieving lot',err);
      throw(err)
    }
    return this;
  }

  async loadVersion() {
    let queryVars = {};
    queryVars['auctionId'] = this.auctionId;
    queryVars['lotId'] = this.lotId;

    try{
      const response = (await API.graphql(graphqlOperation(auctionLotVersionCustom,queryVars))) as {
        data: AuctionLotVersionCustomQuery;
      };
      // console.log('auctionLotVersionCustom',response.data);
      this.version = response.data.auctionLot['version'];
    }
    catch(err){
      console.log('error retrieving lot',err);
      throw(err)
    }
    return this.version;
  }

  async loadCounts() {
    let queryVars = {};
    queryVars['auctionId'] = this.auctionId;
    queryVars['lotId'] = this.lotId;

    try{
      const response = (await API.graphql(graphqlOperation(auctionLotCountsCustom,queryVars))) as {
        data: AuctionLotCountsCustomQuery;
      };
      // console.log('auctionLotCountsCustom',response.data);
      this.bidCount = response.data.auctionLot['bidCount'];
      this.bidderCount = response.data.auctionLot['bidderCount'];
    }
    catch(err){
      console.log('error retrieving lot',err);
      throw(err)
    }
    return this.version;
  }


  async update(){
    if(this.lotId == undefined || this.lotId.length == 0){
      return this.create();
    }

    try{
      let updateVars = {};
      updateVars['auctionId'] = this.auctionId;
      updateVars['lotId'] = this.lotId;
      updateVars['expectedVersion'] = this.version;
      updateVars['input'] = this.getUpdateVars();

      const response = (await API.graphql(graphqlOperation(updateAuctionLot,updateVars))) as {
        data: UpdateAuctionLotMutation;
      };

      if(response.data == undefined || response.data.updateAuctionLot == undefined){
        throw new Error('Error updating auction lot. Contact your administrator.');
      }
      // console.log('auction update',response);
      await this.get(this.auctionId,this.lotId);
      return this;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error updating auction lot',err);
      throw(errorMsg)
    }
  }

  async updateLotPriceParms(floorPrice:number,bidIncrement:number){
    try{
      await this.loadVersion();
      let updateVars = {};
      updateVars['auctionId'] = this.auctionId;
      updateVars['lotId'] = this.lotId;
      updateVars['expectedVersion'] = this.version;
      let nextBid:number = this.currentBid + bidIncrement;
      updateVars['input'] = {"floorPrice":floorPrice,"bidIncrement":bidIncrement, "nextBid":nextBid };

      const response = (await API.graphql(graphqlOperation(updateAuctionLotPriceParmsCustom,updateVars))) as {
        data: UpdateAuctionLotPriceParmsCustomMutation;
      };

      if(response.data == undefined || response.data.updateAuctionLot == undefined){
        throw new Error('Error updating auction lot price parameters. Contact your administrator.');
      }
      // console.log('auction lot price parameters',response);
      this.floorPrice = response.data.updateAuctionLot['floorPrice'];
      this.bidIncrement = response.data.updateAuctionLot['bidIncrement'];
      this.nextBid = response.data.updateAuctionLot['nextBid'];
      this.version = response.data.updateAuctionLot['version'];
      return this;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error updating auction lot',err);
      throw(errorMsg)
    }
  }

  async updateLotNum(){
    try{
      await this.loadVersion();
      let updateVars = {};
      updateVars['auctionId'] = this.auctionId;
      updateVars['lotId'] = this.lotId;
      updateVars['expectedVersion'] = this.version;
      updateVars['input'] = {"lotNum":this.lotNum };

      const response = (await API.graphql(graphqlOperation(updateLotNumberCustom,updateVars))) as {
        data: UpdateAuctionLotNumCustomMutation;
      };

      if(response.data == undefined || response.data.updateAuctionLot == undefined){
        throw new Error('Error updating auction lot number. Contact your administrator.');
      }
      // console.log('auction lot price parameters',response);
      this.lotNum = response.data.updateAuctionLot['lotNum'];
      this.version = response.data.updateAuctionLot['version'];
      return this;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error updating auction lot number',err);
      throw(errorMsg)
    }
  }

  async create(){
    let updateVars = {};
    updateVars['auctionId'] = this.auctionId;
    updateVars['input'] = this.getUpdateVars();

    try{
      const response = (await API.graphql(graphqlOperation(createAuctionLot,updateVars))) as {
        data: CreateAuctionLotMutation;
      };

      if(response.data == undefined || response.data.createAuctionLot == undefined){
        throw new Error('Error creating auction lot. Contact your administrator.');
      }
      // console.log('product create',response);
      this.auctionId = response.data.createAuctionLot.auctionId;
      this.lotId = response.data.createAuctionLot.lotId;
      await this.get(this.auctionId,this.lotId);
      return this;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error creating auction lot',err);
      throw(errorMsg)
    }
  }

  async delete(){

    try{
      let updateVars = {};
      updateVars['auctionId'] = this.auctionId;
      updateVars['lotId'] = this.lotId;

      const response = (await API.graphql(graphqlOperation(deleteAuctionLot,updateVars))) as {
        data: DeleteAuctionLotMutation;
      };

      if(response.data == undefined || response.data.deleteAuctionLot == undefined){
        throw new Error('Error deleting auction lot. Contact your administrator.');
      }
      return response.data.deleteAuctionLot;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error deleting auction lot',err);
      throw(errorMsg)
    }
  }

  async bidNow(){
    var bidPrice = 0;
    if(this.nextBid != undefined){
      bidPrice = this.nextBid;
    }
    else if(this.startBid != undefined){
       bidPrice = this.startBid;
    }

    if(bidPrice > 0){
      try{
        var response = await this.updateBid(bidPrice);
        this.processBidResponse(response);
        return response;
      }
      catch(err){
        var errorMsg = err.errors != undefined ? err.errors[0].message : err;
        console.log('error updating bid',err);
        throw(errorMsg)
      }
    }
  }

  async proxyBidNow(bidderAccountId){
    var bidPrice = 0;
    if(this.nextBid != undefined){
      bidPrice = this.nextBid;
    }
    else if(this.startBid != undefined){
       bidPrice = this.startBid;
    }

    if(bidPrice > 0){
      try{
        var response = await this.updateBid(bidPrice,undefined,bidderAccountId);
        this.processBidResponse(response);
        return response;
      }
      catch(err){
        var errorMsg = err.errors != undefined ? err.errors[0].message : err;
        console.log('error updating bid',err);
        throw(errorMsg)
      }
    }
  }

  processBidResponse(response){
    this.winMsg = "";
    this.outBidMsg = "";
    if(response.code == BidResultCode.BID_SUCCESS){
      this.winMsg = LotModel.WINNING_BID_STR;
    }
    else if(response.code == BidResultCode.BID_TOO_LOW){
      this.outBidMsg = LotModel.OUT_BID_STR;
    }
    else if(response.code == BidResultCode.AUCTION_LOT_CLOSED){
      this.outBidMsg = LotModel.AUCTION_LOT_CLOSED_STR;
    }
    else if(response.code == BidResultCode.NOT_AUTHORIZED){
      this.outBidMsg = LotModel.NOT_AUTHORIZED_TO_BID_STR;
    }
    else {
      this.outBidMsg = LotModel.BID_UNSUCESSFUL_STR;
    }

  }


  async setMaxBid(maxBidPrice){
    try{
      var response = await this.updateBid(undefined,maxBidPrice);
      return response;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error setting max bid',err);
      throw(errorMsg)
    }
  }

  async updateBid(bidPrice,maxBidPrice=undefined,targetAccountId=undefined){

    try{
      this.message = "";
      let updateVars = {};
      updateVars['auctionId'] = this.auctionId;
      updateVars['lotId'] = this.lotId;
      updateVars['bidPrice'] = bidPrice;
      maxBidPrice != undefined ? updateVars['maxBidPrice'] = maxBidPrice : "";
      targetAccountId != undefined ? updateVars['targetAccountId'] = targetAccountId : "";

      const response = (await API.graphql(graphqlOperation(submitBid,updateVars))) as {
        data: SubmitBidMutation;
      };

      // console.log('response for updateBid',response);
      if(response.data == undefined || response.data.submitBid == undefined){
        throw new Error('Error updating bid. Contact your administrator.');
      }

      if(response.data.submitBid.data != undefined){
        this.bidCount = response.data.submitBid.data.bidCount;
        this.bidIncrement = response.data.submitBid.data.bidIncrement;
        this.bidderId = response.data.submitBid.data.bidderId;
        this.currentBid = response.data.submitBid.data.currentBid;
        this.isNewBidderId = response.data.submitBid.data.isNewBidderId;
        this.nextBid = response.data.submitBid.data.nextBid;
      }

      this.message = response.data.submitBid.message;
      this.code = response.data.submitBid.code;
      this.status = response.data.submitBid.status;

      return this;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error updating bid',err);
      throw(errorMsg)
    }
  }

  getUpdateVars(){
    var returnValue = {};
    returnValue['title'] = this.title;
    returnValue['lotOrder'] = this.lotOrder;
    returnValue['biddingDisabled'] = this.biddingDisabled;
    returnValue['openBiddingPeriodMinutes'] = this.openBiddingPeriodMinutes;
    returnValue['customFields'] = this.customFields;
    returnValue['lotNum'] = this.lotNum;
    returnValue['category'] = this.category;
    returnValue['donorName'] = this.donorName;
    returnValue['donorSire'] = this.donorSire;
    returnValue['donorDam'] = this.donorDam;
    returnValue['breed'] = this.breed;
    returnValue['consigner'] = this.consigner;
    returnValue['specialTerms'] = this.specialTerms;
    returnValue['deliveryMethods'] = this.deliveryMethods;
    returnValue['startBid'] = this.startBid;
    returnValue['floorPrice'] = this.floorPrice;
    returnValue['interest'] = this.interest;
    returnValue['embryoType'] = this.embryoType;
    returnValue['quantity'] = this.quantity;
    returnValue['taxable'] = this.taxable;
    returnValue['tag'] = this.tag;
    returnValue['regNum'] = this.regNum;
    returnValue['regUrl'] = this.regUrl;
    returnValue['lineageHierarchy'] = this.lineageHierarchy;
    returnValue['epdMeasures'] = this.epdMeasures;
    returnValue['tattoo'] = this.tattoo;
    returnValue['dob'] = this.dob;
    returnValue['sexedCategory'] = this.sexedCategory;
    returnValue['recipientId'] = this.recipientId;
    returnValue['exportable'] = this.exportable;
    returnValue['footnote'] = this.footnote;
    returnValue['contactInfo'] = this.contactInfo;
    returnValue['pictures'] = this.pictures;
    returnValue['photos'] = this.photos;
    returnValue['videos'] = this.videos;
    returnValue['externalVideoUrl'] = this.externalVideoUrl != undefined && this.externalVideoUrl.trim().length > 0 ? this.externalVideoUrl : null;
    returnValue['bidIncrement'] = this.bidIncrement;
    returnValue['commissionType'] = this.commissionType != undefined ? this.commissionType : null;
    returnValue['commissionValue'] = this.commissionValue != undefined ? this.commissionValue : null;
    returnValue['nextBid'] = this.nextBid;
    returnValue['businessAccountId'] = this.businessAccountId;
    returnValue['actualEPD'] = this.actualEPD;
    returnValue['importError'] = null;
    returnValue['importErrors'] = null;
    return returnValue;
  }

  getLotCoverImage(){
    var storageName = "";
    var returnValue = "";
    if(this.photos != undefined && this.photos.length > 0){
      let coverLotImage = this.photos.find((el)=>{ return el.cover==true});
      if(coverLotImage != undefined){
        storageName = coverLotImage.storageName;
      }
      else {
        storageName = this.photos[0].storageName;
      }
      returnValue = "https://" + environment.image_path_url + "/" + this.lotId + "/" + storageName;
    }
    return returnValue;
  }

  getPhotoUrls(){
    var returnValue = [];
    for(let item of this.photos){
      let url = "https://" + environment.image_path_url + "/" + this.lotId + "/" + item.storageName;
      returnValue.push(url);
    }
    return returnValue;
  }

  getPhotoUrl(key:LotImage){
    let url = "https://" + environment.image_path_url + "/" + this.lotId + "/" + key.storageName;
    return url;
  }

  getVideoUrls(){
    var returnValue = [];
    for(let item of this.videos){
      let url = "https://" + environment.image_path_url + "/" + this.lotId + "/" + item.storageName;
      returnValue.push(url);
    }
    return returnValue;
  }

  getVideoUrl(key:LotImage){
      let url = "https://" + environment.image_path_url + "/" + this.lotId + "/" + key.storageName;
      return url;
  }

  getFirstVideoUrl(){
    var returnValue = "";
    for(let item of this.videos){
      returnValue = "https://" + environment.image_path_url + "/" + this.lotId + "/" + item.storageName;
      break;
    }
    return returnValue;
  }

  async getFileURL(filename:string){

    try{
      const response = (await API.graphql(graphqlOperation(manageFile,{itemId:this.lotId,itemKey:filename,public:true}))) as {
        data: ManageFileMutation;
      };
      // console.log('getFileURL for ',filename," ",response);
      return response.data.manageFile;
    }
    catch(err){
      console.log('error getting file url',err);
      throw(err)
    }
  }

  async cloneAuctionLotAndAssets(auctionId:string,lotId:string){
      let queryVars = {
        auctionId: auctionId,
        lotId: lotId
      }

    try{
      const response = (await API.graphql(graphqlOperation(cloneAuctionLotAndAssets,queryVars))) as {
        data: CloneAuctionLotAndAssetsMutation;
      };

      if(response.data == undefined || response.data.cloneAuctionLotAndAssets == undefined){
        throw new Error('Error cloning auction lot and assets. Contact your administrator.');
      }
      // console.log('cloneAuctionLotAndAssets',response);
      let newLotId = response.data.cloneAuctionLotAndAssets['lotId'];
      return this.get(auctionId,newLotId);
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error cloning auction lot and assets',err);
      throw(errorMsg)
    }
  }

  getInitializedEPDs(){
    var returnValue = [];

    for(let item of LotModel.EPDS_PRODUCTION){
      let epd = {
        category:EPDCategory.PRODUCTION,
        measure:item,
        value:"",
        accuracy:"",
        progeny:"",
        percentage:"",
        order:""
      };
      returnValue.push(epd);
    }

    for(let item of LotModel.EPDS_MATERNAL){
      let epd = {
        category:EPDCategory.MATERNAL,
        measure:item,
        value:"",
        accuracy:"",
        progeny:"",
        percentage:"",
        order:""
      };
      returnValue.push(epd);
    }

    for(let item of LotModel.EPDS_MANAGEMENT){
      let epd = {
        category:EPDCategory.MANAGEMENT,
        measure:item,
        value:"",
        accuracy:"",
        progeny:"",
        percentage:"",
        order:""
      };
      returnValue.push(epd);
    }

    for(let item of LotModel.EPDS_WEIGHT){
      let epd = {
        category:EPDCategory.WEIGHT,
        measure:item,
        value:"",
        accuracy:"",
        progeny:"",
        percentage:"",
        order:""
      };
      returnValue.push(epd);
    }

    for(let item of LotModel.getEPDS_FERTILITY()){
      let epd = {
        category:EPDCategory.FERTILITY,
        measure:item,
        value:"",
        accuracy:"",
        progeny:"",
        percentage:"",
        order:""
      };
      returnValue.push(epd);
    }

    for(let item of LotModel.EPDS_CARCASS){
      let epd = {
        category:EPDCategory.CARCASS,
        measure:item,
        value:"",
        accuracy:"",
        progeny:"",
        percentage:"",
        order:""
      };
      returnValue.push(epd);
    }

    for(let item of LotModel.getEPDS_$VALUES()){
      let epd = {
        category:EPDCategory.DOLLAR_VALUES,
        measure:item,
        value:"",
        accuracy:"",
        progeny:"",
        percentage:"",
        order:""
      };
      returnValue.push(epd);
    }

    return returnValue;

  }

  getInitializedLineage(){
    var returnValue = {
      sire:"",
      sireRegNum:"",
      sireUrl:"",
      dam:"",
      damRegNum:"",
      damUrl:"",
      sireParents : {
        sire:"",
        sireRegNum:"",
        sireUrl:"",
        dam:"",
        damRegNum:"",
        damUrl:"",
        sireParents:undefined,
        damParents:undefined
      },
      damParents : {
        sire:"",
        sireRegNum:"",
        sireUrl:"",
        dam:"",
        damRegNum:"",
        damUrl:"",
        sireParents:undefined,
        damParents:undefined
      }
    };
    return returnValue;

  }

  getLotCategoryFriendlyName(){
    let returnValue = "";
    if(this.category != undefined){
      let item = LotModel.LOT_CATEGORIES.find((el)=>{return el.key==this.category})
      returnValue = item != undefined ? item.value : "";
    }
    return returnValue;
  }

}
export class AuctionReportLineItem {
  lotId: string;
  lotNum: string;
  title: string;
  finalBid: number;
  quantity: number;
  interest: InterestType;
  finalPrice: number;
  saleStatus: SaleStatus;
  invoiceId: string;
  invoice: Invoice;
  businessAccountId: string;
  winningBidderFirstName: string;
  winningBidderLastName: string;
  winningBidderAddress: Array<string> = ["", ""];
  winningBidderEmail: string;
  winningBidderPhoneNumber: string;
  paymentStatus: PaymentStatus;
  sellerName: string;
  sellerPhoneNumber: string;
  sellerEmail: string;
  additionalLineItems: Array<AdjustmentLineItem> = [];
}

export class AdjustmentLineItem {
  amount: number;
  note: string;
}

export class SendInvoicesResult {
  auctionId: string;
  invoiceSentDate: string;
  invoices: Array<Invoice>;
}

export class SendInvoiceResult {
  auctionId: string;
  invoice: Invoice;
}

export class Invoice {
  auctionId: string;
  auctionName: string;
  auctionStartDate: string;
  invoiceId: string;
  invoiceNumber: string;
  invoiceDate: string;
  invoiceSentDate: string;
  paymentDueDate: string;
  bannerImageKey: string;
  buyerName: string;
  buyerAddress: Array< string | null >;
  buyerPhoneNumber: string;
  buyerEmail: string;
  saleAuctionName: string;
  saleAuctionClosingDate: string;
  sellerName: string;
  sellerPhoneNumber: string;
  sellerEmail: string;
  sellerWebsite: string;
  sellerAddress: Array< string | null >;
  sellerContactName: string;
  sellerCity: string;
  sellerState: string;
  sellerZip: string;
  sellerNotes: string;
  totalAmountPaid: number;
  totalAmountDue: number;
  datePaid: string;
  paid: boolean;
  paymentMethod: string;
  checkNumber?: string;
  paymentStatus: string;
  paymentInvoiceId: string;
  paymentCustomerId: string;
  paymentInvoicePdf: string;
  paymentUrl: string;
}

export interface AuctionReportPayment {
  paymentDate: string;
  amount: number;
  fees: number;
}

export class AuctionReportModel {
  public auctionId: string;
  public invoiceSentDate: string;
  public payments: AuctionReportPayment[];
  public auctionName: string;
  public auctionStartDate: string;
  public auctionClosingDate: string;
  public totalGrossSales: number | null;
  public totalBidderCount: number | null;
  public totalLotCount: number | null;
  public version: number;
  public lineItems: AuctionReportLineItem[] = [];

  constructor() { }

  public async get(auctionId: string, sortField: string, sortDirection: string, lotLimit: number, lastItemId: string): Promise<AuctionReportModel> {
    try {
      if (!auctionId) return null;

      let queryVars: AuctionReportCustomQueryVariables = {
        auctionId: auctionId,
        limit: lotLimit,
        sortField: AuctionReportSortField[sortField],
        sortDirection: SortDirection[sortDirection],
      }
      if (lastItemId) {
        queryVars.nextToken = lastItemId;
      }

      var continueLoop = true;
      var auctionReportResult;
      this.lineItems = new Array();
      while(continueLoop){
        const response = (await API.graphql(graphqlOperation(auctionReportCustom, queryVars))) as {
          data: AuctionReportCustomQuery;
        };


        if (response.data == undefined) {
          throw new Error('Error fetching auction report.');
        }

        auctionReportResult = response.data.auctionReport;

        if (auctionReportResult == undefined) {
          return null;
        }

        continueLoop = response.data.auctionReport.nextToken != undefined;
        queryVars['nextToken'] = response.data.auctionReport.nextToken;
        this.lineItems.push(...auctionReportResult.lineItems);
      }


      this.load(auctionReportResult)
      return this;
    }
    catch(err) {
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('Error fetching auction report', err);
      throw(errorMsg);
    }
  }

  public async updateWinningBuyerInfo(lineItem: AuctionReportLineItem): Promise<AuctionReportLineItem> {
    const input = {
      winningBidderFirstName: lineItem.winningBidderFirstName,
      winningBidderLastName: lineItem.winningBidderLastName,
      winningBidderEmail: lineItem.winningBidderEmail,
      winningBidderAddress: lineItem.winningBidderAddress,
      winningBidderPhoneNumber: lineItem.winningBidderPhoneNumber
    }
    return this.updateLineItem(lineItem, input);
  }

  public async bulkUpdateWinningBuyerInfo(
    sellerEmail: string,
    winningBidderEmail: string,
    buyerInfo: {
      winningBidderFirstName: string;
      winningBidderLastName: string;
      winningBidderEmail: string;
      winningBidderAddress: string[];
      winningBidderPhoneNumber: string;
    }
  ): Promise<AuctionReportLineItem[]> {
    const input = {
      winningBidderFirstName: buyerInfo.winningBidderFirstName,
      winningBidderLastName: buyerInfo.winningBidderLastName,
      winningBidderEmail: buyerInfo.winningBidderEmail,
      winningBidderAddress: buyerInfo.winningBidderAddress,
      winningBidderPhoneNumber: buyerInfo.winningBidderPhoneNumber
    };

    let queryVars: BulkUpdateAuctionReportLineItemsMutationVariables = {
      auctionId: this.auctionId,
      sellerEmail: sellerEmail,
      winningBidderEmail: winningBidderEmail,
      input: input
    };

    const response = (await API.graphql(graphqlOperation(bulkUpdateAuctionReportLineItems, queryVars))) as {
      data: BulkUpdateAuctionReportLineItemsMutation;
    };

    const result = response.data.bulkUpdateAuctionReportLineItems;
    if (response.data == undefined) {
      throw new Error('Error performing bulk update.');
    } else if (result == undefined || result.updatedLineItems == undefined) {
      throw new Error('Bulk update returned no result.');
    }

    // Map the GraphQL response to AuctionReportLineItem objects
    // The bulk mutation doesn't return invoice or additionalLineItems, so we initialize them
    // Note: auctionId, auctionName, and auctionStartDate are in the GraphQL response but not in the class
    return result.updatedLineItems
      .filter((item): item is NonNullable<typeof item> => item != null)
      .map((item): AuctionReportLineItem => {
        if (item.interest == null) {
          throw new Error('Interest type is required but was null in bulk update response');
        }
        if (item.saleStatus == null) {
          throw new Error('Sale status is required but was null in bulk update response');
        }
        if (item.paymentStatus == null) {
          throw new Error('Payment status is required but was null in bulk update response');
        }

        return {
          lotId: item.lotId ?? '',
          lotNum: item.lotNum ?? '',
          title: item.title ?? '',
          businessAccountId: item.businessAccountId ?? '',
          winningBidderFirstName: item.winningBidderFirstName ?? '',
          winningBidderLastName: item.winningBidderLastName ?? '',
          winningBidderAddress: item.winningBidderAddress?.filter((addr): addr is string => addr != null) ?? ['', ''],
          winningBidderEmail: item.winningBidderEmail ?? '',
          winningBidderPhoneNumber: item.winningBidderPhoneNumber ?? '',
          finalBid: item.finalBid ?? 0,
          quantity: item.quantity ?? 0,
          interest: item.interest,
          finalPrice: item.finalPrice ?? 0,
          saleStatus: item.saleStatus,
          invoiceId: item.invoiceId ?? '',
          invoice: undefined,
          paymentStatus: item.paymentStatus,
          sellerName: item.sellerName ?? '',
          sellerPhoneNumber: item.sellerPhoneNumber ?? '',
          sellerEmail: item.sellerEmail ?? '',
          additionalLineItems: []
        };
      });
  }

  public async updateSaleStatus(lineItem: AuctionReportLineItem): Promise<AuctionReportLineItem> {
    const input = {
      saleStatus: lineItem.saleStatus
    }
    return this.updateLineItem(lineItem, input);
  }

  public async updatePaymentStatus(lineItem: AuctionReportLineItem): Promise<AuctionReportLineItem> {
    const input = {
      paymentStatus: lineItem.paymentStatus
    }
    return this.updateLineItem(lineItem, input);
  }

  public async adjustLineItemAmounts(lineItem: AuctionReportLineItem): Promise<AuctionReportLineItem> {
    const input = {
      quantity: lineItem.quantity,
      finalPrice: lineItem.finalPrice,
      additionalLineItems: this.trimEmptyLineItems(lineItem.additionalLineItems),
    }

    return this.updateLineItem(lineItem, input);
  }

  public async splitLineItem(lineItem: AuctionReportLineItem, splitData: any): Promise<AuctionReportLineItem> {
    // Split logic:
    // 1. Original line item: Update finalPrice to originalBuyerAmount (reduced from full amount)
    // 2. Create new line items: One for each split user with their amount
    // 3. Validation: originalBuyerAmount + sum(splitUsers.amounts) = original lineItem.finalPrice

    const originalFinalPrice = lineItem.finalPrice;

    // Validate the split totals match
    const splitUsersTotal = splitData.splitUsers.reduce((sum: number, user: any) => sum + (user.amount || 0), 0);
    const totalSplit = (splitData.originalBuyerAmount || 0) + splitUsersTotal;

    if (Math.abs(totalSplit - originalFinalPrice) > 0.01) {
      throw new Error(`Split total ($${totalSplit}) does not equal original price ($${originalFinalPrice})`);
    }

    // Prepare split users input for the mutation
    const splitUsersInput = splitData.splitUsers.map((splitUser: SplitUser) => ({
      firstName: splitUser.firstName,
      lastName: splitUser.lastName,
      email: splitUser.email || null,
      phoneNumber: splitUser.phoneNumber || null,
      address: splitUser.address || null,
      amount: splitUser.amount
    }));

    // Prepare the mutation input
    const input: SplitAuctionReportLineItemInput = {
      splitUsers: splitUsersInput
    };

    // Call the splitAuctionReportLineItem mutation
    // This mutation will:
    // 1. Update the original line item's finalPrice to originalBuyerAmount
    // 2. Create new line items for each split user
    let queryVars: SplitAuctionReportLineItemMutationVariables = {
      auctionId: this.auctionId,
      lotId: lineItem.lotId,
      input: input
    };

    const response = (await API.graphql(graphqlOperation(splitAuctionReportLineItem, queryVars))) as {
      data: SplitAuctionReportLineItemMutation;
    };

    const result: AuctionReportLineItem = response.data.splitAuctionReportLineItem;
    if (response.data == undefined) {
      throw new Error('Error performing split operation.');
    } else if (result == undefined) {
      throw new Error('Split operation returned no result.');
    }

    return result;
  }

  private trimEmptyLineItems(lineItems: AdjustmentLineItem[]) {
    var result: AdjustmentLineItem[] = []
    lineItems.forEach(lineItem => {
      if (lineItem.amount) {
        result.push(lineItem);
      }
    })
    return result;
  }

  public async updatePayments(payments: AuctionReportPayment[]): Promise<AuctionReportModel> {
    let queryVars: UpdateAuctionReportMutationVariables = {
      auctionId: this.auctionId,
      expectedVersion: this.version,
      input: { payments: payments }
    }

    const response = (await API.graphql(graphqlOperation(updateAuctionReport, queryVars))) as {
      data: UpdateAuctionReportMutation;
    };

    const auctionReportResult: any = response.data.updateAuctionReport;
    if (response.data == undefined) {
      throw new Error('Could not update the auction report.');
    } else if (auctionReportResult == undefined) {
      return null;
    }
    return auctionReportResult;
  }

  private async updateLineItem(lineItem: AuctionReportLineItem, fieldInput: any): Promise<AuctionReportLineItem> {
    let queryVars: UpdateAuctionReportLineItemMutationVariables = {
      auctionId: this.auctionId,
      lotId: lineItem.lotId,
      input: fieldInput
    }

    const response = (await API.graphql(graphqlOperation(updateAuctionReportLineItem, queryVars))) as {
      data: UpdateAuctionReportLineItemMutation;
    };

    const auctionReportResult: AuctionReportLineItem = response.data.updateAuctionReportLineItem;
    if (response.data == undefined) {
      throw new Error('Error performing update.');
    } else if (auctionReportResult == undefined) {
      return null;
    }
    return auctionReportResult;
  }

  public async moveLineItemsToDirectPaidStatus() {
    var that = this;
    const updatePaymentStatuses = async() => {
      const promises = (that.lineItems || []).map(async item => {
        item.paymentStatus = PaymentStatus.DIRECT_PAID;
        await that.updatePaymentStatus(item);
      });
      await Promise.all(promises);
    }
    await updatePaymentStatuses();
  }

  public async sendInvoice(sellerKey: string, buyerKey: string) {
    let queryVars: SendInvoiceMutationVariables = {
      auctionId: this.auctionId,
      sellerEmail: sellerKey,
      buyerEmail: buyerKey
    }

    const response = (await API.graphql(graphqlOperation(sendInvoice, queryVars))) as {
      data: SendInvoiceMutation;
    };

    const result: SendInvoiceResult = response.data.sendInvoice;
    if (response.data == undefined) {
      throw new Error('Error performing update.');
    } else if (result == undefined) {
      return null;
    }
    return result;
  }

  public async sendInvoices(): Promise<SendInvoicesResult> {
    let queryVars: SendInvoicesMutationVariables = {
      auctionId: this.auctionId,
    }

    const response = (await API.graphql(graphqlOperation(sendInvoices, queryVars))) as {
      data: SendInvoicesMutation;
    };

    const result: SendInvoicesResult = response.data.sendInvoices;
    if (response.data == undefined) {
      throw new Error('Error performing update.');
    } else if (result == undefined) {
      return null;
    }
    return result;
  }

  load(item: {}) {
    if(item.hasOwnProperty('lineItems')){
      delete item['lineItems'];
    }

    Object.assign(this, item);
    return this;
  }
}
export class BidModel {
  auctionId:string;
  lotId:string;
  bidType: BidType;
  bidderFirstName:string;
  bidderLastName:string;
  price:number;
  bidTime: string;
  bidderAccountId: string;
  bidderId:string;
  parentLotId:string;

  load(item:{}){
    this.auctionId = item['auctionId'] != undefined ? item['auctionId'] : "";
    this.lotId = item['lotId'] != undefined ? item['lotId'] : "";
    this.parentLotId = item['parentLotId'] != undefined ? item['parentLotId'] : "";
    this.bidType = item['bidType'] != undefined ? item['bidType'] : "";
    this.bidderFirstName = item['bidderFirstName'] != undefined ? item['bidderFirstName'] : "";
    this.bidderLastName = item['bidderLastName'] != undefined ? item['bidderLastName'] : "";
    this.price = item['price'] != undefined ? item['price'] : "";
    this.bidTime = item['bidTime'] != undefined ? item['bidTime'] : "";
    this.bidderAccountId = item['bidderAccountId'] != undefined ? item['bidderAccountId'] : "";
    this.bidderId = item['bidderId'] != undefined ? item['bidderId'] : undefined;
    return this;
  }

  isAdmin(){
    return this.bidType == BidType.ADMIN;
  }

  async delete(){

    try{
      let updateVars = {};
      updateVars['auctionId'] = this.auctionId;
      updateVars['lotId'] = this.parentLotId;
      updateVars['bidTime'] = this.bidTime;

      const response = (await API.graphql(graphqlOperation(deleteBid,updateVars))) as {
        data: DeleteBidMutation;
      };

      // console.log('response for deleteBid',response);
      if(response.data == undefined || response.data.deleteBid == undefined){
        throw new Error('Error deleting bid. Contact your administrator.');
      }
      return response.data.deleteBid;
    }
    catch(err){
      var errorMsg = err.errors != undefined ? err.errors[0].message : err;
      console.log('error deleting bid',err);
      throw(errorMsg)
    }
  }

}

export class BidderModel {
  auctionId:string;
  lotId:string;
  bidderFirstName:string;
  bidderLastName:string;
  bidderAccountId: string;

  load(item:{}){
    this.auctionId = item['auctionId'] != undefined ? item['auctionId'] : "";
    this.lotId = item['lotId'] != undefined ? item['lotId'] : "";
    this.bidderFirstName = item['bidderFirstName'] != undefined ? item['bidderFirstName'] : "";
    this.bidderLastName = item['bidderLastName'] != undefined ? item['bidderLastName'] : "";
    this.bidderAccountId = item['bidderAccountId'] != undefined ? item['bidderAccountId'] : "";
    return this;
  }

}
